import { useRef, useCallback, RefObject } from 'react';

/**
 * Hook to handle modal overlay clicks with a buffer zone
 * Prevents accidental modal closures when:
 * - Clicking just outside the modal while copy/pasting
 * - Starting a selection inside and releasing outside the modal
 *
 * Only closes the modal if BOTH mousedown and mouseup happen outside the buffer zone
 *
 * @param onClose - Callback to execute when clicking outside the buffer zone
 * @param bufferPx - Buffer zone in pixels around the modal content (default: 30px)
 * @returns [modalContentRef, handleOverlayClick, handleOverlayMouseDown] - Ref for modal content, click handler, and mousedown handler for overlay
 */
export function useModalOverlayClick<T extends HTMLElement = HTMLDivElement>(
  onClose: () => void,
  bufferPx: number = 30
): [RefObject<T>, (e: React.MouseEvent) => void, (e: React.MouseEvent) => void] {
  const modalContentRef = useRef<T>(null);
  // Use null to indicate "not tracked yet" - defaults to old behavior if onMouseDown is not used
  const mouseDownOutsideRef = useRef<boolean | null>(null);

  const handleOverlayMouseDown = useCallback(
    (e: React.MouseEvent) => {
      // Only track if mousedown is on the overlay itself (not a child element)
      if (e.target !== e.currentTarget) {
        // Mousedown was on modal content, so definitely don't close
        mouseDownOutsideRef.current = false;
        return;
      }

      // Get modal content bounds
      if (!modalContentRef.current) {
        // No modal content, so mousedown was outside
        mouseDownOutsideRef.current = true;
        return;
      }

      const rect = modalContentRef.current.getBoundingClientRect();
      const mouseDownX = e.clientX;
      const mouseDownY = e.clientY;

      // Check if mousedown is within buffer zone around modal content
      const inBufferZone =
        mouseDownX >= rect.left - bufferPx &&
        mouseDownX <= rect.right + bufferPx &&
        mouseDownY >= rect.top - bufferPx &&
        mouseDownY <= rect.bottom + bufferPx;

      // Track whether mousedown happened outside the buffer zone
      mouseDownOutsideRef.current = !inBufferZone;
    },
    [bufferPx]
  );

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      // Only close if clicking directly on the overlay (not a child element)
      if (e.target !== e.currentTarget) {
        return;
      }

      // Get modal content bounds
      if (!modalContentRef.current) {
        onClose();
        return;
      }

      const rect = modalContentRef.current.getBoundingClientRect();
      const clickX = e.clientX;
      const clickY = e.clientY;

      // Check if click (mouseup) is within buffer zone around modal content
      const inBufferZone =
        clickX >= rect.left - bufferPx &&
        clickX <= rect.right + bufferPx &&
        clickY >= rect.top - bufferPx &&
        clickY <= rect.bottom + bufferPx;

      // If mousedown tracking is enabled (not null), only close if BOTH mousedown and mouseup happened outside
      // If mousedown tracking is not enabled (null), use old behavior (close if mouseup is outside)
      const shouldClose = !inBufferZone && (mouseDownOutsideRef.current === null || mouseDownOutsideRef.current === true);

      if (shouldClose) {
        onClose();
      }

      // Reset the tracker
      mouseDownOutsideRef.current = null;
    },
    [onClose, bufferPx]
  );

  return [modalContentRef, handleOverlayClick, handleOverlayMouseDown];
}
